/* eslint-disable no-plusplus */
import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { mediaQuery, Spacings } from '@styles/index';
import styled from '@emotion/styled';
import { getAccessToken, getMe } from '@selectors/auth';
import { getMeLatestUserSubscriptions } from '@selectors/subscription';
import Me, { hasActiveTrialSubscriptionToGoal } from '@models/Me';
import ExploreGoals from '@cont/ManageGoals/ExploreGoals';
import SecondSection from '@cont/Homepage/SecondSection';
import fetch from '@utils/api-helper/fetch';
import pushRoute from '@utils/pushRoute';
import segmentStorage from '@events/segmentStorage';
import { segment } from '@actions/analytics';
import { useSnackbar } from '@stores/snackbar';
import H2 from '@base/H2/H2';
import {
  FREE_TRIAL_ACTIVATED,
  FREE_TRIAL_GOAL_DROPDOWN_CLICKED,
  FREE_TRIAL_GOAL_SELECTED,
  FREE_TRIAL_GOAL_SELECTION_CONFIRMED,
  FREE_TRIAL_PAGE_VIEWED,
  FREE_TRIAL_REFERRAL_AAPLIED
} from './constants/segment';
import HomeScreen from './HomeScreen';
import GoalConfirmation from './GoalConfirmation';
import TrialActive from './TrialActive';
import TrialOver from './TrialOver';
import TrialReferralInput from './TrialReferralInput';
import {
  getGrantTrialEndpoint,
  getValidateReferralEndpoint
} from './apiHelper';

const Container = styled.div`
  padding-top: 52px;
  max-width: 1136px;
  margin: auto;
`;

const DividerSection = styled.div`
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_0B};
  text-align: left;
  margin-bottom: ${Spacings.SPACING_6B};
  h4 {
    line-height: ${Spacings.SPACING_6B};
    font-size: ${Spacings.SPACING_5B};
  }
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_4B}
      ${Spacings.SPACING_0B};
  }
`;

const StyledSecondSection = styled(SecondSection)`
  text-align: left;
  align-items: left !important;
`;

const getQueryParamsFromUrl = (url) => {
  const query = {};
  new URL(url).searchParams.forEach((value, key) => {
    query[key] = value;
  });
  return query;
};

export const SCREEN_STATES = {
  DEFAULT: 'DEFAULT',
  SELECT_GOAL: 'SELECT_GOAL',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REFERRAL_CODE: 'REFERRAL_CODE',
  ALREADY_GRANTED: 'ALREADY_GRANTED'
};

const getGoalDetails = (goals, goalUid) => {
  if (!goalUid) return null;
  for (let i = 0; i < goals.length; i++) {
    const { children } = goals[i];
    for (let j = 0; j < children.length; j++) {
      const goal = children[j];

      if (!goal.children) {
        if (goal.uid === goalUid.toUpperCase()) {
          return goal;
        }
      } else {
        const goalDetails = goal.children?.find(
          (subGoal) => subGoal.uid === goalUid.toUpperCase()
        );

        if (goalDetails) {
          return goalDetails;
        }
      }
    }
  }
};

const FreeTrial = ({ onboardingGoals }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const me = useSelector(getMe);
  const { referral_code, goal_uid } = getQueryParamsFromUrl(
    `https://ua.com${router.asPath}`
  );
  const goalDetails = getGoalDetails(onboardingGoals, goal_uid);
  const [referralCode, setReferralCode] = useState(referral_code);
  const isLoggedIn = useSelector(getAccessToken);
  const [screenState, setScreenState] = useState(SCREEN_STATES.DEFAULT);
  const [selectedGoal, setSelectedGoal] = useState(goalDetails);
  const [activateClicked, setActivateClicked] = useState(false);
  const [isReferralValid, setIsReferralValid] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [grantResponse, setGrantResponse] = useState(null);
  const [isInProgress, setIsInProgress] = useState(false);
  const latestUserSubscriptions = useSelector(getMeLatestUserSubscriptions);
  const { getState } = useStore();
  const { open: showSnackbar } = useSnackbar();

  useEffect(() => {
    segmentStorage.setItem('lps', 'Free Trial');
    segment.track(FREE_TRIAL_PAGE_VIEWED, {
      free_trial_source: referral_code || 'NA'
    });
  }, [referral_code]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screenState]);

  const referralApplyEvent = (code, isValid) => {
    segment.track(FREE_TRIAL_REFERRAL_AAPLIED, {
      free_trial_source: referralCode,
      referral_code_applied: code,
      is_valid_refferal_code: isValid
    });
  };

  const checkReferralCodeValidity = useCallback(
    (code) => {
      setReferralCode(code);
      fetch(getValidateReferralEndpoint(), {
        getState,
        requestType: 'POST',
        body: {
          referral_code: code
        }
      })
        .then(() => {
          setIsReferralValid(true);
          referralApplyEvent(code, true);
        })
        .catch(() => {
          setIsReferralValid(false);
          referralApplyEvent(code, false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getState]
  );

  const updateUrlGoal = (goalUid) => {
    if (goal_uid) {
      router.replace(
        '/subscription/free-trial/',
        `/subscription/free-trial/?${
          referral_code
            ? `referral_code=${encodeURIComponent(referralCode)}`
            : ''
        }&${`goal_uid=${encodeURIComponent(goalUid)}`}`,
        { shallow: true }
      );
    }
  };

  const selectGoal = useCallback(() => {
    if (goalDetails) {
      setScreenState(SCREEN_STATES.REFERRAL_CODE);
      return;
    }
    if (referral_code) {
      setScreenState(SCREEN_STATES.SELECT_GOAL);
      checkReferralCodeValidity(referral_code);
    } else {
      setScreenState(SCREEN_STATES.SELECT_GOAL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral_code, checkReferralCodeValidity]);

  useEffect(() => {
    if (activateClicked && isLoggedIn) {
      selectGoal();
    }
  }, [isLoggedIn, activateClicked, selectGoal]);

  const onGoalSelection = useCallback(
    (goal) => {
      setSelectedGoal(goal);
      if (isReferralValid && referralCode && !goal_uid) {
        setShowConfirmationModal(true);
      } else {
        setScreenState(SCREEN_STATES.REFERRAL_CODE);
      }

      updateUrlGoal(goal.uid);

      segment.track(FREE_TRIAL_GOAL_SELECTED, {
        free_trial_source: referralCode || 'NA',
        goal_name: goal.name,
        goal_uid: goal.uid
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isReferralValid, referralCode]
  );

  const triggerFreeTrialActivatedEvent = (state, code) => {
    segment.track(FREE_TRIAL_ACTIVATED, {
      free_trial_source: code || referralCode || 'NA',
      free_trial_Status: state,
      goal_name: selectedGoal.name,
      goal_uid: selectedGoal.uid
    });
  };

  const goToGoalScreen = useCallback(() => {
    segment.track(FREE_TRIAL_GOAL_DROPDOWN_CLICKED, {
      free_trial_source: referralCode || 'NA'
    });
    setScreenState(SCREEN_STATES.SELECT_GOAL);
  }, [referralCode]);

  const activateFreeTrial = useCallback(
    (code = referralCode) => {
      const activeSubscription = Me.getActiveSubscription(
        latestUserSubscriptions,
        selectedGoal?.uid
      );
      const isTrialSubscription = hasActiveTrialSubscriptionToGoal(
        me,
        selectedGoal?.uid
      );
      const activeSubscriptionExceptTrial =
        activeSubscription && !isTrialSubscription;

      if (activeSubscriptionExceptTrial) {
        pushRoute({
          pathname: '/goal/[goalSlug]/[goalUID]/browse',
          query: {
            goalSlug: selectedGoal.slug || '-',
            goalUID: selectedGoal.uid
          }
        });
      } else {
        setIsInProgress(true);
        fetch(getGrantTrialEndpoint(), {
          getState,
          requestType: 'POST',
          body: {
            goal: selectedGoal.uid,
            referral_code: code
          }
        }).then((data) => {
          if (!data.error) {
            setGrantResponse({
              subscription_end_date: data.user_subscription.expired_at
            });
            setScreenState(SCREEN_STATES.ACTIVE);
            setIsInProgress(false);
            triggerFreeTrialActivatedEvent('New', code);
          } else {
            data.error.response.json().then((resp) => {
              setIsInProgress(false);
              if (resp.status === 'TrialSubscriptionAlreadyGranted') {
                setGrantResponse(resp.meta);
                if (new Date() < new Date(resp.meta.subscription_end_date)) {
                  setScreenState(SCREEN_STATES.ALREADY_GRANTED);
                  triggerFreeTrialActivatedEvent('Ongoing', code);
                } else {
                  setScreenState(SCREEN_STATES.EXPIRED);
                  triggerFreeTrialActivatedEvent('Completed', code);
                }
              } else {
                showSnackbar({
                  message: resp.message,
                  type: 'error'
                });
              }
            });
          }
        });
      }
      segment.track(FREE_TRIAL_GOAL_SELECTION_CONFIRMED, {
        free_trial_source: code || referralCode || 'NA',
        goal_name: selectedGoal.name,
        goal_uid: selectedGoal.uid
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [referralCode, selectedGoal, dispatch, getState]
  );

  return (
    <Container>
      {screenState === SCREEN_STATES.DEFAULT && (
        <HomeScreen
          selectGoal={selectGoal}
          setActivateClicked={setActivateClicked}
          setScreenState={setScreenState}
          referralCode={referralCode}
          isGoalPresent={!!goalDetails}
        />
      )}
      {screenState === SCREEN_STATES.SELECT_GOAL && (
        <>
          <ExploreGoals
            isOnboarding
            onboardingGoals={onboardingGoals}
            onGoalSelection={onGoalSelection}
            isFreeTrial
          />
          {showConfirmationModal && (
            <GoalConfirmation
              onSubmit={() => activateFreeTrial()}
              selectedGoal={selectedGoal}
              referralCode={referralCode}
              closeModal={setShowConfirmationModal}
              isInProgress={isInProgress}
            />
          )}
        </>
      )}
      {(screenState === SCREEN_STATES.ACTIVE ||
        screenState === SCREEN_STATES.ALREADY_GRANTED) && (
        <TrialActive
          goal={selectedGoal}
          grantResponse={grantResponse}
          referralCode={referralCode}
          isAlreadyActive={screenState === SCREEN_STATES.ALREADY_GRANTED}
        />
      )}
      {screenState === SCREEN_STATES.EXPIRED && (
        <TrialOver subscription={selectedGoal} grantResponse={grantResponse} />
      )}
      {screenState === SCREEN_STATES.REFERRAL_CODE && (
        <TrialReferralInput
          goal={selectedGoal}
          goToGoalScreen={goToGoalScreen}
          referralCode={referral_code}
          isReferralValid={isReferralValid}
          checkReferralCodeValidity={checkReferralCodeValidity}
          activateFreeTrial={activateFreeTrial}
          isInProgress={isInProgress}
        />
      )}
      {screenState !== SCREEN_STATES.SELECT_GOAL &&
        screenState !== SCREEN_STATES.REFERRAL_CODE && (
          <>
            <DividerSection>
              <H2>A complete learning experience for your preparation</H2>
            </DividerSection>
            <StyledSecondSection alignment="left" textAfterImage />
          </>
        )}
    </Container>
  );
};

export default FreeTrial;
